/**
 * Enhanced Analytics Tracking Utility
 * Tracks page views, conversions, traffic sources, and user journeys
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Get or create visitor ID (persists across sessions for unique visitor tracking)
const getVisitorId = () => {
  let visitorId = localStorage.getItem('analytics_visitor_id');
  if (!visitorId) {
    visitorId = 'vis_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
    localStorage.setItem('analytics_visitor_id', visitorId);
  }
  return visitorId;
};

// Get or create session ID (new per browser session)
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('analytics_session_id');
  if (!sessionId) {
    sessionId = 'sess_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
    sessionStorage.setItem('analytics_session_id', sessionId);
    // Mark session start time
    sessionStorage.setItem('analytics_session_start', Date.now().toString());
  }
  return sessionId;
};

// Parse UTM parameters from URL
const getUTMParams = () => {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get('utm_source') || null,
    utm_medium: params.get('utm_medium') || null,
    utm_campaign: params.get('utm_campaign') || null,
    utm_term: params.get('utm_term') || null,
    utm_content: params.get('utm_content') || null,
    fbclid: params.get('fbclid') || null,
    gclid: params.get('gclid') || null,
  };
};

// Parse referrer to identify traffic source
const parseReferrer = (referrer) => {
  if (!referrer) return { source: 'direct', medium: 'none' };
  
  try {
    const url = new URL(referrer);
    const hostname = url.hostname.toLowerCase();
    
    // Social media
    if (hostname.includes('facebook.com') || hostname.includes('fb.com')) {
      return { source: 'facebook', medium: 'social' };
    }
    if (hostname.includes('instagram.com')) {
      return { source: 'instagram', medium: 'social' };
    }
    if (hostname.includes('twitter.com') || hostname.includes('t.co')) {
      return { source: 'twitter', medium: 'social' };
    }
    if (hostname.includes('linkedin.com')) {
      return { source: 'linkedin', medium: 'social' };
    }
    if (hostname.includes('youtube.com')) {
      return { source: 'youtube', medium: 'social' };
    }
    if (hostname.includes('whatsapp.com') || hostname.includes('wa.me')) {
      return { source: 'whatsapp', medium: 'social' };
    }
    
    // Search engines
    if (hostname.includes('google.')) {
      return { source: 'google', medium: 'organic' };
    }
    if (hostname.includes('bing.com')) {
      return { source: 'bing', medium: 'organic' };
    }
    if (hostname.includes('yahoo.com')) {
      return { source: 'yahoo', medium: 'organic' };
    }
    
    // Same site (mothering.in)
    if (hostname.includes('mothering.in')) {
      return { source: 'internal', medium: 'internal' };
    }
    
    // Other referrers
    return { source: hostname, medium: 'referral' };
  } catch {
    return { source: 'unknown', medium: 'unknown' };
  }
};

// Get device info
const getDeviceInfo = () => {
  const ua = navigator.userAgent;
  
  // Device type
  let deviceType = 'desktop';
  if (/Mobi|Android/i.test(ua)) {
    deviceType = 'mobile';
  } else if (/Tablet|iPad/i.test(ua)) {
    deviceType = 'tablet';
  }
  
  // Browser
  let browser = 'other';
  if (ua.includes('Chrome') && !ua.includes('Edg')) browser = 'chrome';
  else if (ua.includes('Safari') && !ua.includes('Chrome')) browser = 'safari';
  else if (ua.includes('Firefox')) browser = 'firefox';
  else if (ua.includes('Edg')) browser = 'edge';
  else if (ua.includes('Opera') || ua.includes('OPR')) browser = 'opera';
  
  // OS
  let os = 'other';
  if (ua.includes('Windows')) os = 'windows';
  else if (ua.includes('Mac')) os = 'macos';
  else if (ua.includes('Linux')) os = 'linux';
  else if (ua.includes('Android')) os = 'android';
  else if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad')) os = 'ios';
  
  return { deviceType, browser, os };
};

// Store landing page info (first page of session)
const getLandingPage = () => {
  let landingPage = sessionStorage.getItem('analytics_landing_page');
  if (!landingPage) {
    landingPage = window.location.pathname + window.location.search;
    sessionStorage.setItem('analytics_landing_page', landingPage);
  }
  return landingPage;
};

// Track page view with enhanced data
export const trackPageView = async (page, path, productId = null, productName = null) => {
  try {
    const utmParams = getUTMParams();
    const referrerInfo = parseReferrer(document.referrer);
    const deviceInfo = getDeviceInfo();
    const visitorId = getVisitorId();
    const sessionId = getSessionId();
    const landingPage = getLandingPage();
    
    // Determine traffic source (UTM takes priority over referrer)
    let trafficSource = referrerInfo.source;
    let trafficMedium = referrerInfo.medium;
    
    if (utmParams.utm_source) {
      trafficSource = utmParams.utm_source;
      trafficMedium = utmParams.utm_medium || 'campaign';
    } else if (utmParams.fbclid) {
      trafficSource = 'facebook';
      trafficMedium = 'paid';
    } else if (utmParams.gclid) {
      trafficSource = 'google';
      trafficMedium = 'cpc';
    }
    
    await fetch(`${API_URL}/api/analytics/track/pageview`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        page,
        path,
        referrer: document.referrer,
        user_agent: navigator.userAgent,
        session_id: sessionId,
        visitor_id: visitorId,
        product_id: productId,
        product_name: productName,
        // Traffic source data
        traffic_source: trafficSource,
        traffic_medium: trafficMedium,
        utm_campaign: utmParams.utm_campaign,
        utm_content: utmParams.utm_content,
        utm_term: utmParams.utm_term,
        // Device data
        device_type: deviceInfo.deviceType,
        browser: deviceInfo.browser,
        os: deviceInfo.os,
        // Screen info
        screen_width: window.innerWidth,
        screen_height: window.innerHeight,
        // Session data
        landing_page: landingPage,
        is_new_visitor: !localStorage.getItem('analytics_returning'),
      }),
    });
    
    // Mark as returning visitor after first pageview
    localStorage.setItem('analytics_returning', 'true');
  } catch (error) {
    console.log('Analytics tracking error:', error);
  }
};

// Track conversion events
export const trackEvent = async (eventType, data = {}) => {
  try {
    await fetch(`${API_URL}/api/analytics/track/event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event_type: eventType,
        product_id: data.productId,
        product_name: data.productName,
        value: data.value,
        order_id: data.orderId,
        session_id: getSessionId(),
        visitor_id: getVisitorId(),
      }),
    });
  } catch (error) {
    console.log('Analytics event tracking error:', error);
  }
};

// Heartbeat to track active users (call every 30 seconds while user is on site)
let heartbeatInterval = null;
export const startHeartbeat = () => {
  if (heartbeatInterval) return;
  
  const sendHeartbeat = async () => {
    try {
      await fetch(`${API_URL}/api/analytics/track/heartbeat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: getSessionId(),
          visitor_id: getVisitorId(),
          current_page: window.location.pathname,
        }),
      });
    } catch (error) {
      // Silent fail
    }
  };
  
  // Send immediately
  sendHeartbeat();
  
  // Then every 30 seconds
  heartbeatInterval = setInterval(sendHeartbeat, 30000);
};

export const stopHeartbeat = () => {
  if (heartbeatInterval) {
    clearInterval(heartbeatInterval);
    heartbeatInterval = null;
  }
};

// Convenience methods
export const trackAddToCart = (productId, productName, value) => {
  trackEvent('add_to_cart', { productId, productName, value });
};

export const trackCheckout = (value) => {
  trackEvent('checkout', { value });
};

export const trackPurchase = (orderId, value) => {
  trackEvent('purchase', { orderId, value });
};

export default {
  trackPageView,
  trackEvent,
  trackAddToCart,
  trackCheckout,
  trackPurchase,
  getSessionId,
  getVisitorId,
  startHeartbeat,
  stopHeartbeat,
};
